import { User } from '@supabase/supabase-js'
import {
  BaseCaption,
  Caption,
  TextToMemeGeneratedResponse,
  ThemedMemeGeneratedResponse,
  UserDetails,
} from '@supermeme-ai/types'

export interface StyleResponse {
  url: string
}

export interface SearchResponse {
  memeTemplates: Array<{
    name: string
    image_path: string
    description: string
    meme_text: string
  }>
}

export interface MyMemesResponse {
  page: number
  pageSize: number
  results: any[]
  totalResultCount: number
}

export interface MyPromptsResponse {
  page: number
  pageSize: number
  prompts: any[]
  totalPromptCount: number
}

export interface MyTemplatesResponse {
  templates: string[]
  totalTemplateCount: number
}

export interface PromptResultsResponse {
  results: any[]
}

export interface StarResponse {
  success: boolean
}

export interface UsedCreditsResponse {
  usedCreditCount: number
}

export interface UserDetailsResponse {
  userDetails: UserDetails
}

class GeneralApiClient {
  private baseUrl: string
  private authToken: string | null = null

  constructor() {
    // Use environment variable or fallback
    this.baseUrl = process.env.NEXT_PUBLIC_GENERAL_API_URL
  }

  setAuthToken(token: string | null) {
    this.authToken = token
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.message || `API request failed with status ${response.status}`
      )
    }

    return response.json()
  }

  // Text-to-meme endpoint
  async convertTextToMeme(
    text: string,
    maxDimension: number,
    inputLanguage?: string,
    outputLanguage?: string
  ): Promise<TextToMemeGeneratedResponse> {
    return this.makeRequest<TextToMemeGeneratedResponse>(
      '/api/v1/meme/text-to-meme',
      {
        method: 'POST',
        body: JSON.stringify({
          text,
          maxDimension,
          inputLanguage,
          outputLanguage,
        }),
      }
    )
  }

  // Text-to-gif endpoint
  async convertTextToGif(
    text: string,
    maxDimension: number,
    imageCount?: number,
    inputLanguage?: string,
    outputLanguage?: string
  ): Promise<TextToMemeGeneratedResponse> {
    return this.makeRequest<TextToMemeGeneratedResponse>(
      '/api/v1/meme/text-to-gif',
      {
        method: 'POST',
        body: JSON.stringify({
          text,
          maxDimension,
          imageCount,
          inputLanguage,
          outputLanguage,
        }),
      }
    )
  }

  // Style endpoint
  async getStyledImageUrl(
    image: string,
    style?: string
  ): Promise<StyleResponse> {
    const params = new URLSearchParams({ image })
    if (style) {
      params.append('style', style)
    }

    return this.makeRequest<StyleResponse>(
      `/api/v1/meme/style?${params.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  // Themed template endpoint
  async generateThemedTemplate(
    imageName: string,
    memeTheme: string,
    maxDimension: number,
    imageDescription: string,
    inputLanguage?: string,
    outputLanguage?: string,
    originalDescription?: string
  ): Promise<ThemedMemeGeneratedResponse> {
    return this.makeRequest<ThemedMemeGeneratedResponse>(
      '/api/v1/meme/themed/template',
      {
        method: 'POST',
        body: JSON.stringify({
          imageName,
          memeTheme,
          maxDimension,
          imageDescription,
          inputLanguage,
          outputLanguage,
          originalDescription,
        }),
      }
    )
  }

  // Search endpoint
  async searchMemeTemplates(searchQuery: string): Promise<SearchResponse> {
    const params = new URLSearchParams({ searchQuery })

    return this.makeRequest<SearchResponse>(
      `/api/v1/search?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          // Include token in header for search history
          ...(this.authToken && { token: this.authToken }),
        },
      }
    )
  }

  // Account endpoints
  async getMyMemes(
    page: number = 1,
    pageSize: number = 10,
    sort: string = 'created_at'
  ): Promise<MyMemesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      sort,
    })

    return this.makeRequest<MyMemesResponse>(
      `/api/v1/account/my-memes?${params.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  async getMyPrompts(
    page: number = 1,
    pageSize: number = 10,
    sort: string = 'created_at'
  ): Promise<MyPromptsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      sort,
    })

    return this.makeRequest<MyPromptsResponse>(
      `/api/v1/account/my-prompts?${params.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  async getMyTemplates(
    page: number = 1,
    pageSize: number = 10,
    sort: string = 'created_at'
  ): Promise<MyTemplatesResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      sort,
    })

    return this.makeRequest<MyTemplatesResponse>(
      `/api/v1/account/my-templates?${params.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  async getPromptResults(promptId: string): Promise<PromptResultsResponse> {
    const params = new URLSearchParams({ promptId })

    return this.makeRequest<PromptResultsResponse>(
      `/api/v1/account/prompt-results?${params.toString()}`,
      {
        method: 'GET',
      }
    )
  }

  // Result endpoints
  async updateResult(
    resultId: number,
    captions: Caption[],
    header: BaseCaption,
    footer: BaseCaption
  ): Promise<void> {
    return this.makeRequest(`/api/v1/result/${resultId}`, {
      method: 'PATCH',
      body: JSON.stringify({ captions, header, footer }),
    })
  }

  async starResult(resultId: number): Promise<StarResponse> {
    return this.makeRequest<StarResponse>('/api/v1/result/star', {
      method: 'POST',
      body: JSON.stringify({ resultId }),
    })
  }

  async unstarResult(resultId: number): Promise<StarResponse> {
    return this.makeRequest<StarResponse>('/api/v1/result/unstar', {
      method: 'POST',
      body: JSON.stringify({ resultId }),
    })
  }

  async getUsedCredits(): Promise<UsedCreditsResponse> {
    return this.makeRequest<UsedCreditsResponse>(
      '/api/v1/account/used-credits',
      {
        method: 'GET',
      }
    )
  }

  async getUserDetails(): Promise<UserDetailsResponse> {
    return this.makeRequest<UserDetailsResponse>(
      '/api/v1/account/user-details',
      {
        method: 'GET',
      }
    )
  }
}

// Create singleton instance
const generalApiClient = new GeneralApiClient()

export { generalApiClient }

// Helper function to set up client with user authentication
export const setupGeneralApiClient = (
  user: User | null,
  token: string | null
) => {
  generalApiClient.setAuthToken(token)
  return generalApiClient
}
